import type { FcAdminOperation, FcAdminStatusData } from '@shared/fcAdminApiTypes';

export type FcAdminAccess = {
    authorized: boolean;
    mutationReady: boolean;
    capabilities: ReadonlySet<FcAdminOperation>;
};

type FcAdminStatusContract = FcAdminStatusData & {
    dataFreshness?: 'live' | 'cached' | 'degraded' | string;
    mutationReady?: boolean;
};

export type FcAdminLiveStatus = FcAdminStatusData & {
    transient?: boolean;
    transientCode?: string;
};

export type FcAdminStatusResult =
    | { ok: true; data: FcAdminStatusData }
    | { ok: false; code: string; transient?: unknown; retryAfterMs?: number };

export type FcAdminStatusPlan = {
    action: 'apply' | 'keep' | 'degrade';
    transientCode?: string;
    retryAfterMs?: number;
    status?: FcAdminLiveStatus;
};

export const fcAdminTransientKeepLimitMs = 60_000;

const definitiveDenials = new Set([
    'actor_not_linked',
    'discord_not_linked',
    'not_linked',
    'no_fc_team_role',
    'not_member',
    'forbidden',
]);

const transientStatusCodes = new Set([
    'rate_limited',
    'service_starting',
    'broker_timeout',
    'broker_unavailable',
    'discord_rate_limited',
    'request_cancelled',
]);

export const isTransientFcAdminStatusFailure = (failure?: { code?: string; transient?: unknown }) => (
    !!failure && (failure.transient === true || (typeof failure.code === 'string' && transientStatusCodes.has(failure.code)))
);

export const hasFcAdminReadAccess = (status?: FcAdminStatusData) => (
    status?.discordLinked === true && status.actorAuthorized === true
);

export const isDefinitiveFcAdminAccessDenial = (status?: FcAdminStatusData) => (
    !!status && definitiveDenials.has(status.authorizationState)
);

export const fcAdminStatusTransientCode = (status?: FcAdminStatusData) => {
    if (!status) return undefined;
    const live = status as FcAdminLiveStatus;
    if (live.transient === true) return typeof live.transientCode === 'string' && live.transientCode ? live.transientCode : status.authorizationState;
    return !hasFcAdminReadAccess(status) && transientStatusCodes.has(status.authorizationState) ? status.authorizationState : undefined;
};

export const isTransientFcAdminStatus = (status?: FcAdminStatusData) => fcAdminStatusTransientCode(status) !== undefined;

export const isFcAdminStatusLive = (status?: FcAdminStatusData) => (
    !!status
    && (status.authorizationState === 'ready' || status.authorizationState === 'authorized')
    && !isTransientFcAdminStatus(status)
);

export const selectFcAdminReadStatus = (
    previous: FcAdminStatusData | undefined,
    current: FcAdminStatusData,
) => {
    if (hasFcAdminReadAccess(current)) return current;
    if (isDefinitiveFcAdminAccessDenial(current)) return undefined;
    return previous;
};

export const planFcAdminStatusResult = (
    previous: FcAdminStatusData | undefined,
    result: FcAdminStatusResult,
    transientSince: number | undefined,
    now: number,
): FcAdminStatusPlan => {
    const transientCode = result.ok
        ? fcAdminStatusTransientCode(result.data)
        : isTransientFcAdminStatusFailure(result) ? result.code : undefined;
    if (!transientCode) return { action: result.ok ? 'apply' : 'degrade' };
    const retryAfterMs = result.ok ? result.data.retryAfterMs : result.retryAfterMs;
    if (result.ok && hasFcAdminReadAccess(result.data)) return { action: 'apply', transientCode, retryAfterMs };
    const keepable = !!previous
        && hasFcAdminReadAccess(previous)
        && (transientSince === undefined || now - transientSince <= fcAdminTransientKeepLimitMs);
    if (!keepable) return { action: result.ok ? 'apply' : 'degrade', transientCode, retryAfterMs };
    const previousMutationReady = deriveFcAdminAccess(previous).mutationReady;
    return {
        action: 'keep',
        transientCode,
        retryAfterMs,
        status: {
            ...previous,
            ...(result.ok ? { gameServerOnline: result.data.gameServerOnline, resourceOnline: result.data.resourceOnline } : {}),
            dataFreshness: 'cached',
            mutationReady: result.ok
                ? previousMutationReady && result.data.gameServerOnline && result.data.resourceOnline
                : previousMutationReady,
            retryAfterMs,
            transient: true,
            transientCode,
        },
    };
};

export const deriveFcAdminAccess = (status?: FcAdminStatusData): FcAdminAccess => {
    const authorized = hasFcAdminReadAccess(status);
    const contract = status as FcAdminStatusContract | undefined;
    const capabilities = new Set<FcAdminOperation>();
    if (authorized) {
        for (const capability of status?.capabilities ?? []) {
            if (capability.allowed) capabilities.add(capability.operation);
        }
    }
    return {
        authorized,
        mutationReady: authorized && (typeof contract?.mutationReady === 'boolean'
            ? contract.mutationReady
            : status?.bridgeConfigured === true
                && status.gameServerOnline === true
                && status.resourceOnline === true
                && status.offlineSnapshot === undefined
                && (contract?.dataFreshness === undefined || contract.dataFreshness === 'live')),
        capabilities,
    };
};

export const fcAdminAccessFingerprint = (status?: FcAdminStatusData) => {
    if (!status) return 'unavailable';
    const capabilities = status.capabilities
        .map((entry) => `${entry.operation}:${entry.allowed ? '1' : '0'}:${entry.clientRequired ? '1' : '0'}`)
        .sort()
        .join(',');
    const permissions = [...(status.actor?.permissions ?? [])].sort().join(',');
    return [
        status.discordLinked ? '1' : '0',
        status.actorAuthorized ? '1' : '0',
        status.authorizationState,
        status.actor?.name ?? '',
        status.actor?.role ?? '',
        status.actor?.isMaster ? '1' : '0',
        status.actor?.isTxAdminMaster ? '1' : '0',
        permissions,
        capabilities,
    ].join('|');
};

export type FcAdminDashboardRequestGuard = {
    epoch: number;
    route: string;
    accessRevision: string;
    allowed: boolean;
};

export const isFcAdminDashboardResponseCurrent = (
    request: FcAdminDashboardRequestGuard,
    current: FcAdminDashboardRequestGuard,
) => request.epoch === current.epoch
    && request.route === '/'
    && current.route === request.route
    && current.accessRevision === request.accessRevision
    && request.allowed
    && current.allowed;
